var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { ref, watch, onUnmounted, unref, defineComponent, computed, openBlock, createBlock, resolveDynamicComponent, mergeProps, withCtx, renderSlot, createElementBlock, Fragment, renderList, normalizeProps, shallowRef, createElementVNode, guardReactiveProps, toRefs, createVNode, createCommentVNode } from "vue";
const LayoutTypes = ["rows", "columns", "masonry"];
const breakpoints = Object.freeze([1200, 600, 300, 0]);
function unwrap(value, arg) {
  return typeof value === "function" ? value(arg) : value;
}
function unwrapParameter(value, containerWidth) {
  return typeof value !== "undefined" ? unwrap(value, containerWidth) : void 0;
}
function selectResponsiveValue(values, containerWidth) {
  const index2 = breakpoints.findIndex(
    (breakpoint) => breakpoint <= containerWidth
  );
  return unwrap(values[index2 >= 0 ? index2 : 0], containerWidth);
}
function resolveResponsiveParameter(parameter, containerWidth, values, minValue = 0) {
  const value = unwrapParameter(parameter, containerWidth);
  return Math.round(
    Math.max(
      value === void 0 ? selectResponsiveValue(values, containerWidth) : value,
      minValue
    )
  );
}
function resolveContainerWidth(element, breakpoints2) {
  let width = element?.clientWidth;
  if (width !== void 0 && breakpoints2 !== void 0 && breakpoints2.length > 0) {
    const sorted = breakpoints2.filter((bp) => bp > 0).sort((a, b) => b - a);
    sorted.push(Math.floor(sorted[sorted.length - 1] / 2));
    const threshold = width;
    width = sorted.find(
      (breakpoint, index2) => breakpoint <= threshold || index2 === sorted.length - 1
    );
  }
  return width;
}
const unwrapRefElement = (ref2) => {
  const element = unref(ref2);
  return element === void 0 || element instanceof HTMLElement ? element : element.$el;
};
function useContainerWidth(target, breakpoints2, defaultContainerWidth) {
  const containerWidth = ref(defaultContainerWidth);
  const element = ref();
  let observer;
  const updateWidth = () => {
    containerWidth.value = resolveContainerWidth(element.value, breakpoints2);
  };
  const disconnect = () => {
    if (observer === void 0) {
      return;
    }
    observer.disconnect();
    observer = void 0;
  };
  const connect = (element2) => {
    disconnect();
    if (element2 === void 0 || typeof ResizeObserver === "undefined") {
      return;
    }
    observer = new ResizeObserver(updateWidth);
    observer.observe(element2);
  };
  watch(
    target,
    (targetValue) => {
      if (targetValue !== void 0) {
        element.value = unwrapRefElement(target);
        connect(element.value);
      } else {
        disconnect();
      }
    },
    {
      immediate: true,
      flush: "post"
    }
  );
  onUnmounted(() => {
    disconnect();
  });
  return { containerWidth };
}
function round(value, digits = 0) {
  const factor = 10 ** digits;
  const preciseValue = value + Number.EPSILON;
  return Math.round(preciseValue * factor) / factor;
}
function ratio({ width, height }) {
  return width / height;
}
function rankingFunctionComparator(rank) {
  return (a, b) => rank(b) - rank(a);
}
class Heap {
  constructor(comparator) {
    __publicField(this, "heap", []);
    __publicField(this, "n", 0);
    this.comparator = comparator;
  }
  greater(i, j) {
    return this.comparator(this.heap[i], this.heap[j]) < 0;
  }
  swap(i, j) {
    const temp = this.heap[i];
    this.heap[i] = this.heap[j];
    this.heap[j] = temp;
  }
  swim(i) {
    let k = i;
    let k2 = Math.floor(k / 2);
    while (k > 1 && this.greater(k2, k)) {
      this.swap(k2, k);
      k = k2;
      k2 = Math.floor(k / 2);
    }
  }
  sink(i) {
    let k = i;
    let k2 = k * 2;
    while (k2 <= this.n) {
      if (k2 < this.n && this.greater(k2, k2 + 1)) {
        k2 += 1;
      }
      if (!this.greater(k, k2)) {
        break;
      }
      this.swap(k, k2);
      k = k2;
      k2 = k * 2;
    }
  }
  push(element) {
    this.n += 1;
    this.heap[this.n] = element;
    this.swim(this.n);
  }
  pop() {
    if (this.n === 0) {
      return void 0;
    }
    this.swap(1, this.n);
    this.n -= 1;
    const max = this.heap.pop();
    this.sink(1);
    return max;
  }
  get size() {
    return this.n;
  }
}
function buildPrecedentsMap(graph, startNode, endNode) {
  const precedentsMap = /* @__PURE__ */ new Map();
  const visited = /* @__PURE__ */ new Set();
  const storedShortestPaths = /* @__PURE__ */ new Map();
  storedShortestPaths.set(startNode, 0);
  const queue = new Heap(
    rankingFunctionComparator((el) => el.weight)
  );
  queue.push({ id: startNode, weight: 0 });
  while (queue.size > 0) {
    const { id, weight } = queue.pop();
    if (visited.has(id)) {
      continue;
    }
    const neighboringNodes = graph(id);
    visited.add(id);
    neighboringNodes.forEach((neighborWeight, neighbor) => {
      const accWeight = weight + neighborWeight;
      const currentId = precedentsMap.get(neighbor);
      const currentWeight = storedShortestPaths.get(neighbor);
      if (currentWeight === void 0 || currentWeight > accWeight && (currentWeight / accWeight > 1.005 || currentId !== void 0 && currentId < id)) {
        storedShortestPaths.set(neighbor, accWeight);
        queue.push({ id: neighbor, weight: accWeight });
        precedentsMap.set(neighbor, id);
      }
    });
  }
  return storedShortestPaths.has(endNode) ? precedentsMap : void 0;
}
function getPathFromPrecedentsMap(precedentsMap, endNode) {
  const nodes = [];
  for (let node = endNode; node !== void 0; node = precedentsMap.get(node)) {
    nodes.push(node);
  }
  return nodes.reverse();
}
function finrShortestPath(graph, startNode, endNode) {
  const precedentsMap = buildPrecedentsMap(graph, startNode, endNode);
  return precedentsMap !== void 0 ? getPathFromPrecedentsMap(precedentsMap, endNode) : void 0;
}
function findIdeaNodeSearch({
  photos,
  targetRowHeight,
  containerWidth
}) {
  const minRatio = photos.reduce(
    (acc, photo) => Math.min(acc, ratio(photo)),
    Number.MAX_VALUE
  );
  return round(containerWidth / targetRowHeight / minRatio) + 2;
}
function getCommonHeight(row, containerWidth, spacing, padding) {
  const rowWidth = containerWidth - (row.length - 1) * spacing - 2 * padding * row.length;
  const totalAspectRatio = row.reduce((acc, photo) => acc + ratio(photo), 0);
  return rowWidth / totalAspectRatio;
}
function cost(photos, i, j, width, targetRowHeight, spacing, padding) {
  const row = photos.slice(i, j);
  const commonHeight = getCommonHeight(row, width, spacing, padding);
  if (commonHeight <= 0) {
    return void 0;
  }
  return (commonHeight - targetRowHeight) ** 2 * row.length;
}
function makeGetRowNeighbors({
  photos,
  layoutOptions,
  targetRowHeight,
  limitNodeSearch,
  rowConstraints
}) {
  return (node) => {
    const { containerWidth, spacing, padding } = layoutOptions;
    const results = /* @__PURE__ */ new Map();
    results.set(node, 0);
    const startOffset = rowConstraints?.minPhotos ?? 1;
    const endOffset = Math.min(
      limitNodeSearch,
      rowConstraints?.maxPhotos ?? Number.POSITIVE_INFINITY
    );
    for (let i = node + startOffset; i < photos.length + 1; i += 1) {
      if (i - node > endOffset) {
        break;
      }
      const currentCost = cost(
        photos,
        node,
        i,
        containerWidth,
        targetRowHeight,
        spacing,
        padding
      );
      if (currentCost === void 0) {
        break;
      }
      results.set(i, currentCost);
    }
    return results;
  };
}
function computeRowsLayout({
  photos,
  layoutOptions
}) {
  const { spacing, padding, containerWidth, targetRowHeight, rowConstraints } = layoutOptions;
  const limitNodeSearch = findIdeaNodeSearch({
    photos,
    containerWidth,
    targetRowHeight
  });
  const getNeighbors = makeGetRowNeighbors({
    photos,
    layoutOptions,
    targetRowHeight,
    limitNodeSearch,
    rowConstraints
  });
  const path = finrShortestPath(getNeighbors, 0, photos.length);
  if (path === void 0) {
    return void 0;
  }
  const layout = [];
  for (let i = 1; i < path.length; i += 1) {
    const row = photos.map((photo, index2) => ({ photo, index: index2 })).slice(path[i - 1], path[i]);
    const height = getCommonHeight(
      row.map(({ photo }) => photo),
      containerWidth,
      spacing,
      padding
    );
    layout.push(
      row.map(({ photo, index: index2 }, photoIndex) => {
        return {
          photo,
          layout: {
            height,
            width: height * ratio(photo),
            index: index2,
            photoIndex,
            photosCount: row.length
          }
        };
      })
    );
  }
  return layout;
}
const className$1 = "photo-album__row";
const _sfc_main$6 = /* @__PURE__ */ defineComponent({
  __name: "RowRenderer",
  props: {
    layoutOptions: {},
    rowData: {},
    rowIndex: {},
    rowsCount: {},
    renderer: {}
  },
  setup(__props) {
    const props = __props;
    const style = computed(() => {
      const display = "flex";
      const flexFlow = "row nowrap";
      const alignItems = "start";
      const justifyContent = "space-between";
      const marginBottom = props.rowsCount !== void 0 && props.rowIndex < props.rowsCount - 1 ? `${props.layoutOptions.spacing}px` : void 0;
      return { display, flexFlow, alignItems, justifyContent, marginBottom };
    });
    const rowWrapper = computed(() => props.renderer ?? "div");
    const metadata = computed(() => {
      return {
        layoutOptions: props.layoutOptions,
        rowData: props.rowData,
        rowIndex: props.rowIndex,
        rowsCount: props.rowsCount
      };
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(rowWrapper.value), mergeProps({
        class: className$1,
        style: style.value
      }, metadata.value), {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 16, ["style"]);
    };
  }
});
const _sfc_main$5 = /* @__PURE__ */ defineComponent({
  __name: "RowsLayout",
  props: {
    photos: {},
    layoutOptions: {},
    rowRenderer: {}
  },
  setup(__props) {
    const props = __props;
    const rowsLayout = computed(
      () => computeRowsLayout({
        photos: props.photos,
        layoutOptions: props.layoutOptions
      })
    );
    return (_ctx, _cache) => {
      return openBlock(true), createElementBlock(Fragment, null, renderList(rowsLayout.value, (row, rowIndex) => {
        return openBlock(), createBlock(_sfc_main$6, {
          key: `row-${rowIndex}`,
          "layout-options": _ctx.layoutOptions,
          "row-data": row,
          "row-index": rowIndex,
          "rows-count": rowsLayout.value?.length ?? 0,
          renderer: _ctx.rowRenderer
        }, {
          default: withCtx(() => [
            (openBlock(true), createElementBlock(Fragment, null, renderList(row, ({ photo, layout }) => {
              return renderSlot(_ctx.$slots, "default", normalizeProps(mergeProps({
                key: photo.key || photo.src
              }, { photo, layout, layoutOptions: _ctx.layoutOptions })));
            }), 128))
          ]),
          _: 2
        }, 1032, ["layout-options", "row-data", "row-index", "rows-count", "renderer"]);
      }), 128);
    };
  }
});
function computeShortestPath(graph, pathLength, startNode, endNode) {
  const matrix = /* @__PURE__ */ new Map();
  const queue = /* @__PURE__ */ new Set();
  queue.add(startNode);
  for (let length = 0; length < pathLength; length += 1) {
    const currentQueue = [...queue.keys()];
    queue.clear();
    currentQueue.forEach((node) => {
      const accWeight = length > 0 ? matrix.get(node)[length].weight : 0;
      graph(node).forEach(({ neighbor, weight }) => {
        let paths = matrix.get(neighbor);
        if (paths === void 0) {
          paths = [];
          matrix.set(neighbor, paths);
        }
        const newWeight = accWeight + weight;
        const nextPath = paths[length + 1];
        if (nextPath === void 0 || nextPath.weight > newWeight && (nextPath.weight / newWeight > 1.0001 || node < nextPath.node)) {
          paths[length + 1] = { node, weight: newWeight };
        }
        if (length < pathLength - 1 && neighbor !== endNode) {
          queue.add(neighbor);
        }
      });
    });
  }
  return matrix;
}
function reconstructShortestPath(matrix, pathLength, endNode) {
  const path = [endNode];
  for (let node = endNode, length = pathLength; length > 0; length -= 1) {
    node = matrix.get(node)[length].node;
    path.push(node);
  }
  return path.reverse();
}
function findShortestPathLengthN(graph, pathLength, startNode, endNode) {
  return reconstructShortestPath(
    computeShortestPath(graph, pathLength, startNode, endNode),
    pathLength,
    endNode
  );
}
function makeGetColumnNeighbors({
  photos,
  spacing,
  padding,
  targetColumnWidth,
  targetColumnHeight
}) {
  return (node) => {
    const results = [];
    const cutOffHeight = targetColumnHeight * 1.5;
    let height = targetColumnWidth / ratio(photos[node]) + 2 * padding;
    for (let i = node + 1; i < photos.length + 1; i += 1) {
      results.push({ neighbor: i, weight: (targetColumnHeight - height) ** 2 });
      if (height > cutOffHeight || i === photos.length) {
        break;
      }
      height += targetColumnWidth / ratio(photos[i]) + spacing + 2 * padding;
    }
    return results;
  };
}
function buildColumnsModel({
  path,
  photos,
  containerWidth,
  columnsGaps,
  columnsRatios,
  spacing,
  padding
}) {
  const columnsModel = [];
  const totalRatio = columnsRatios.reduce(
    (acc, columnRatio) => acc + columnRatio,
    0
  );
  for (let i = 0; i < path.length - 1; i += 1) {
    const column = photos.map((photo, index2) => ({ photo, index: index2 })).slice(path[i], path[i + 1]);
    const totalAdjustedGaps = columnsRatios.reduce(
      (acc, columnRatio, index2) => acc + (columnsGaps[i] - columnsGaps[index2]) * columnRatio,
      0
    );
    const columnWidth = (containerWidth - (path.length - 2) * spacing - 2 * (path.length - 1) * padding - totalAdjustedGaps) * columnsRatios[i] / totalRatio;
    columnsModel.push(
      column.map(({ photo, index: index2 }, photoIndex) => {
        return {
          photo,
          layout: {
            width: columnWidth,
            height: columnWidth / ratio(photo),
            index: index2,
            photoIndex,
            photosCount: column.length
          }
        };
      })
    );
  }
  return columnsModel;
}
function computeColumnsModel({
  photos,
  layoutOptions,
  targetColumnWidth
}) {
  const { columns, spacing, padding, containerWidth } = layoutOptions;
  const columnsGaps = [];
  const columnsRatios = [];
  if (photos.length <= columns) {
    const averageRatio = photos.length > 0 ? photos.reduce((acc, photo) => acc + ratio(photo), 0) / photos.length : 1;
    for (let i = 0; i < columns; i += 1) {
      columnsGaps[i] = 2 * padding;
      columnsRatios[i] = i < photos.length ? ratio(photos[i]) : averageRatio;
    }
    const columnsModel2 = buildColumnsModel({
      path: Array.from(
        { length: columns + 1 },
        (_, index2) => Math.min(index2, photos.length)
      ),
      photos,
      columnsGaps,
      columnsRatios,
      containerWidth,
      spacing,
      padding
    });
    return { columnsGaps, columnsRatios, columnsModel: columnsModel2 };
  }
  const targetColumnHeight = (photos.reduce((acc, photo) => acc + targetColumnWidth / ratio(photo), 0) + spacing * (photos.length - columns) + 2 * padding * photos.length) / columns;
  const getNeighbors = makeGetColumnNeighbors({
    photos,
    targetColumnWidth,
    targetColumnHeight,
    spacing,
    padding
  });
  const path = findShortestPathLengthN(getNeighbors, columns, 0, photos.length);
  for (let i = 0; i < path.length - 1; i += 1) {
    const column = photos.slice(path[i], path[i + 1]);
    columnsGaps[i] = spacing * (column.length - 1) + 2 * padding * column.length;
    columnsRatios[i] = 1 / column.reduce((acc, photo) => acc + 1 / ratio(photo), 0);
  }
  const columnsModel = buildColumnsModel({
    path,
    photos,
    columnsGaps,
    columnsRatios,
    containerWidth,
    spacing,
    padding
  });
  return { columnsGaps, columnsRatios, columnsModel };
}
function computeLayout({
  photos,
  layoutOptions
}) {
  const { columns, spacing, padding, containerWidth } = layoutOptions;
  const targetColumnWidth = (containerWidth - spacing * (columns - 1) - 2 * padding * columns) / columns;
  const { columnsGaps, columnsRatios, columnsModel } = computeColumnsModel({
    photos,
    layoutOptions,
    targetColumnWidth
  });
  if (columnsModel.findIndex(
    (columnModel) => columnModel.findIndex(
      ({ layout: { width, height } }) => width < 0 || height < 0
    ) >= 0
  ) >= 0) {
    if (columns > 1) {
      return computeLayout({
        photos,
        layoutOptions: { ...layoutOptions, columns: columns - 1 }
      });
    }
    return void 0;
  }
  return { columnsGaps, columnsRatios, columnsModel };
}
function computeColumnsLayout({
  photos,
  layoutOptions
}) {
  return computeLayout({ photos, layoutOptions });
}
const className = "photo-album__column";
const _sfc_main$4 = /* @__PURE__ */ defineComponent({
  __name: "ColumnRenderer",
  props: {
    layoutOptions: {},
    columnData: {},
    columnIndex: {},
    columnsCount: {},
    columnsGaps: {},
    columnsRatios: {},
    renderer: {}
  },
  setup(__props) {
    const props = __props;
    function cssJustifyContent(layoutOptions) {
      return layoutOptions.layout === "columns" ? "space-between" : "flex-start";
    }
    function cssColumnWidth({
      layoutOptions,
      columnIndex,
      columnsCount,
      columnsGaps,
      columnsRatios
    }) {
      const { layout, spacing, padding } = layoutOptions;
      if (layout === "masonry" || columnsGaps === void 0 || columnsRatios === void 0) {
        return `calc((100% - ${spacing * (columnsCount - 1)}px) / ${columnsCount})`;
      }
      const totalRatio = columnsRatios.reduce((acc, ratio2) => acc + ratio2, 0);
      const totalAdjustedGaps = columnsRatios.reduce(
        (acc, ratio2, index2) => acc + (columnsGaps[columnIndex] - columnsGaps[index2]) * ratio2,
        0
      );
      return `calc((100% - ${round(
        (columnsCount - 1) * spacing + 2 * columnsCount * padding + totalAdjustedGaps,
        3
      )}px) * ${round(columnsRatios[columnIndex] / totalRatio, 5)} + ${2 * padding}px)`;
    }
    const style = computed(() => {
      const display = "flex";
      const flexFlow = "column nowrap";
      const alignItems = "flex-start";
      const justifyContent = cssJustifyContent(props.layoutOptions);
      const width = cssColumnWidth(props);
      return { display, flexFlow, alignItems, justifyContent, width };
    });
    const columnWrapper = computed(() => props.renderer ?? "div");
    const metadata = computed(() => {
      return {
        layoutOptions: props.layoutOptions,
        columnData: props.columnData,
        columnIndex: props.columnIndex,
        columnsCount: props.columnsCount,
        columnsGaps: props.columnsGaps,
        columnsRatios: props.columnsRatios
      };
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(columnWrapper.value), mergeProps({
        class: className,
        style: style.value
      }, metadata.value), {
        default: withCtx(() => [
          renderSlot(_ctx.$slots, "default")
        ]),
        _: 3
      }, 16, ["style"]);
    };
  }
});
const _sfc_main$3 = /* @__PURE__ */ defineComponent({
  __name: "ColumnsLayout",
  props: {
    photos: {},
    layoutOptions: {},
    columnRenderer: {}
  },
  setup(__props) {
    const props = __props;
    const columnsLayout = computed(
      () => computeColumnsLayout({
        photos: props.photos,
        layoutOptions: props.layoutOptions
      })
    );
    const columnsModel = computed(() => {
      return columnsLayout.value?.columnsModel;
    });
    const columnsCount = computed(() => columnsModel.value?.length ?? 0);
    const columnsGaps = computed(() => columnsLayout.value?.columnsGaps);
    const columnsRatios = computed(() => columnsLayout.value?.columnsRatios);
    return (_ctx, _cache) => {
      return openBlock(true), createElementBlock(Fragment, null, renderList(columnsModel.value, (column, columnIndex) => {
        return openBlock(), createBlock(_sfc_main$4, {
          key: `column-${columnIndex}`,
          "layout-options": _ctx.layoutOptions,
          "column-data": column,
          "column-index": columnIndex,
          "columns-count": columnsCount.value,
          "columns-gaps": columnsGaps.value,
          "columns-ratios": columnsRatios.value,
          renderer: _ctx.columnRenderer
        }, {
          default: withCtx(() => [
            (openBlock(true), createElementBlock(Fragment, null, renderList(column, ({ photo, layout }) => {
              return renderSlot(_ctx.$slots, "default", normalizeProps(mergeProps({
                key: photo.key || photo.src
              }, { photo, layout, layoutOptions: _ctx.layoutOptions })));
            }), 128))
          ]),
          _: 2
        }, 1032, ["layout-options", "column-data", "column-index", "columns-count", "columns-gaps", "columns-ratios", "renderer"]);
      }), 128);
    };
  }
});
function computeMasonryLayout(props) {
  const { photos, layoutOptions } = props;
  const { containerWidth, spacing, padding, columns } = layoutOptions;
  const columnWidth = (containerWidth - spacing * (columns - 1) - 2 * padding * columns) / columns;
  if (columnWidth <= 0) {
    return columns > 1 ? computeMasonryLayout({
      ...props,
      layoutOptions: { ...layoutOptions, columns: columns - 1 }
    }) : void 0;
  }
  const columnsCurrentTopPositions = Array.from({ length: columns }, () => 0);
  const columnsModel = photos.reduce(
    (model, photo, index2) => {
      const shortestColumnIndex = columnsCurrentTopPositions.reduce(
        (currentShortestColumn, item, itemIndex) => item < columnsCurrentTopPositions[currentShortestColumn] - 1 ? itemIndex : currentShortestColumn,
        0
      );
      columnsCurrentTopPositions[shortestColumnIndex] += columnWidth / ratio(photo) + spacing + 2 * padding;
      model[shortestColumnIndex].push({ photo, index: index2 });
      return model;
    },
    Array.from({ length: columns }, () => [])
  );
  return columnsModel.map((column) => {
    return column.map(({ photo, index: index2 }, photoIndex) => {
      const layout = {
        width: columnWidth,
        height: columnWidth / ratio(photo),
        photosCount: column.length,
        index: index2,
        photoIndex
      };
      return { photo, layout };
    });
  });
}
const _sfc_main$2 = /* @__PURE__ */ defineComponent({
  __name: "MasonryLayout",
  props: {
    photos: {},
    layoutOptions: {},
    columnRenderer: {}
  },
  setup(__props) {
    const props = __props;
    const masonryLayout = computed(
      () => computeMasonryLayout({
        photos: props.photos,
        layoutOptions: props.layoutOptions
      })
    );
    const columnsCount = computed(() => masonryLayout.value?.length ?? 0);
    return (_ctx, _cache) => {
      return openBlock(true), createElementBlock(Fragment, null, renderList(masonryLayout.value, (column, columnIndex) => {
        return openBlock(), createBlock(_sfc_main$4, {
          key: `masonry-column-${columnIndex}`,
          "layout-options": _ctx.layoutOptions,
          "column-data": column,
          "column-index": columnIndex,
          "columns-count": columnsCount.value,
          renderer: _ctx.columnRenderer
        }, {
          default: withCtx(() => [
            (openBlock(true), createElementBlock(Fragment, null, renderList(column, ({ photo, layout }) => {
              return renderSlot(_ctx.$slots, "default", normalizeProps(mergeProps({
                key: photo.key || photo.src
              }, { photo, layout, layoutOptions: _ctx.layoutOptions })));
            }), 128))
          ]),
          _: 2
        }, 1032, ["layout-options", "column-data", "column-index", "columns-count", "renderer"]);
      }), 128);
    };
  }
});
const _sfc_main$1 = /* @__PURE__ */ defineComponent({
  __name: "PhotoRenderer",
  props: {
    photo: {},
    layout: {},
    layoutOptions: {},
    clickable: { type: Boolean },
    renderer: {}
  },
  setup(__props) {
    const props = __props;
    function calcWidth(base, { width, photosCount }, { spacing, padding, containerWidth }) {
      const gaps = spacing * (photosCount - 1) + 2 * padding * photosCount;
      return `calc((${base} - ${gaps}px) / ${round(
        (containerWidth - gaps) / width,
        5
      )})`;
    }
    function cssPhotoWidth(layout, layoutOptions) {
      return layoutOptions.layout !== "rows" ? `calc(100% - ${2 * layoutOptions.padding}px)` : calcWidth("100%", layout, layoutOptions);
    }
    function calculateSizesValue(size, layout, layoutOptions) {
      return calcWidth(
        size.match(/calc\((.*)\)/)?.[1] ?? size,
        layout,
        layoutOptions
      );
    }
    function srcSetAndSizes(photo, layout, layoutOptions) {
      const images = photo.srcSet;
      const srcset = images !== void 0 && images.length > 0 ? images.concat(
        images.find(({ width }) => width === photo.width) !== void 0 ? [{ src: photo.src, width: photo.width, height: photo.height }] : []
      ).sort((a, b) => a.width - b.width).map((image) => `${image.src} ${image.width}w`).join(", ") : void 0;
      const sizes = layoutOptions?.sizes !== void 0 ? (layoutOptions.sizes.sizes ?? []).map(
        ({ viewport, size }) => `${viewport} ${calculateSizesValue(size, layout, layoutOptions)}`
      ).concat(
        calculateSizesValue(layoutOptions.sizes.size, layout, layoutOptions)
      ).join(", ") : `${Math.ceil(layout.width / layoutOptions.containerWidth * 100)}vw`;
      return { srcset, sizes };
    }
    const wrapperStyle = computed(() => {
      const width = cssPhotoWidth(props.layout, props.layoutOptions);
      const aspectRatio = props.photo.width / props.photo.height;
      const padding = `${props.layoutOptions.padding}px`;
      const marginBottom = ["columns", "masonry"].includes(props.layoutOptions.layout) && props.layout.photoIndex < props.layout.photosCount - 1 ? `${props.layoutOptions.spacing}px` : void 0;
      const cursor = props.clickable ? "pointer" : void 0;
      return {
        display: "block",
        boxSizing: "content-box",
        padding,
        marginBottom,
        aspectRatio,
        width,
        height: "auto",
        cursor
      };
    });
    const wrappedStyle = shallowRef({
      display: "block",
      boxSizing: "content-box",
      width: "100%",
      height: "100%"
    });
    const imageProps = computed(() => {
      const className2 = "photo-album__photo";
      const src = props.photo.src;
      const alt = props.photo.alt;
      const title = props.photo.title;
      const style = props.renderer === void 0 ? wrapperStyle.value : wrappedStyle.value;
      const loading = "lazy";
      const decoding = "async";
      return {
        class: className2,
        src,
        alt,
        title,
        style,
        loading,
        decoding,
        ...srcSetAndSizes(props.photo, props.layout, props.layoutOptions)
      };
    });
    const metadata = computed(() => ({
      layoutOptions: props.layoutOptions,
      photo: props.photo,
      layout: props.layout,
      clickable: props.clickable,
      imageProps: imageProps.value
    }));
    return (_ctx, _cache) => {
      return _ctx.renderer ? (openBlock(), createBlock(resolveDynamicComponent(_ctx.renderer), mergeProps({
        key: 0,
        style: wrapperStyle.value
      }, metadata.value), {
        default: withCtx(() => [
          createElementVNode("img", normalizeProps(guardReactiveProps(imageProps.value)), null, 16)
        ]),
        _: 1
      }, 16, ["style"])) : (openBlock(), createElementBlock("img", normalizeProps(mergeProps({ key: 1 }, imageProps.value)), null, 16));
    };
  }
});
const _hoisted_1 = { key: 0 };
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "PhotoAlbum",
  props: {
    photos: {},
    layout: {},
    columns: {},
    spacing: {},
    padding: {},
    targetRowHeight: {},
    rowConstraints: {},
    sizes: {},
    breakpoints: {},
    defaultContainerWidth: {},
    onClick: {},
    containerRenderer: {},
    rowRenderer: {},
    columnRenderer: {},
    photoRenderer: {}
  },
  emits: ["click"],
  setup(__props, { emit: __emit }) {
    const [Rows, Columns, Masonry] = LayoutTypes;
    const props = __props;
    const {
      photos,
      layout,
      targetRowHeight,
      rowConstraints,
      columns,
      spacing,
      padding,
      sizes,
      breakpoints: breakpoints2,
      defaultContainerWidth,
      containerRenderer,
      rowRenderer,
      columnRenderer,
      photoRenderer
    } = toRefs(props);
    const emit = __emit;
    function resolveLayoutOptions({
      layout: layout2,
      containerWidth: containerWidth2,
      targetRowHeight: targetRowHeight2,
      rowConstraints: rowConstraints2,
      columns: columns2,
      spacing: spacing2,
      padding: padding2,
      sizes: sizes2
    }) {
      const commonOptions = {
        layout: layout2,
        containerWidth: containerWidth2,
        spacing: resolveResponsiveParameter(
          spacing2,
          containerWidth2,
          [20, 15, 10, 5]
        ),
        padding: resolveResponsiveParameter(
          padding2,
          containerWidth2,
          [0, 0, 0, 0, 0]
        ),
        sizes: sizes2
      };
      const rowsOptions = {
        targetRowHeight: resolveResponsiveParameter(
          targetRowHeight2,
          containerWidth2,
          [(w) => w / 5, (w) => w / 4, (w) => w / 3, (w) => w / 2]
        ),
        rowConstraints: unwrapParameter(rowConstraints2, containerWidth2)
      };
      const columnsOptions = {
        columns: resolveResponsiveParameter(
          columns2,
          containerWidth2,
          [5, 4, 3, 2],
          1
        )
      };
      switch (layout2) {
        case Rows: {
          return Object.assign(commonOptions, rowsOptions, {
            layout: Rows
          });
        }
        case Columns:
        case Masonry: {
          return Object.assign({}, commonOptions, columnsOptions, {
            layout: layout2 === Columns ? Columns : Masonry
          });
        }
        default:
          return void 0;
      }
    }
    const containerRef = ref();
    const { containerWidth } = useContainerWidth(
      containerRef,
      breakpoints2.value,
      defaultContainerWidth.value
    );
    const layoutOptions = ref();
    const isUnknownLayout = computed(() => {
      return layout.value === void 0 || !LayoutTypes.includes(layout.value);
    });
    const className2 = computed(() => [
      "photo-album",
      `photo-album-${isUnknownLayout.value ? "unknown" : layout.value}`
    ]);
    const isRowsLayoutOptions = (options) => {
      return options?.layout === Rows;
    };
    const style = computed(() => {
      let limitedWidth = void 0;
      if (layout.value === Rows && isRowsLayoutOptions(layoutOptions.value)) {
        const { padding: padding2, spacing: spacing2, rowConstraints: rowConstraints2, targetRowHeight: targetRowHeight2 } = layoutOptions.value;
        const singleRowMaxHeight = rowConstraints2?.singleRowMaxHeight;
        if (singleRowMaxHeight !== void 0) {
          const maxHeight = targetRowHeight2 !== void 0 ? Math.max(singleRowMaxHeight, targetRowHeight2) : singleRowMaxHeight;
          const photoLen = photos.value.length;
          const initWidth = padding2 * photoLen * 2 + spacing2 * (photoLen - 1);
          limitedWidth = Math.floor(
            photos.value.reduce((acc, photo) => {
              return acc + photo.width / photo.height * maxHeight - padding2 * 2;
            }, initWidth)
          );
        }
      }
      const maxWidth = limitedWidth !== void 0 && limitedWidth > 0 ? `${limitedWidth}px` : void 0;
      return {
        display: "flex",
        flexDirection: layout.value === "rows" ? "column" : "row",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        maxWidth
      };
    });
    const containerComponent = computed(() => {
      return {
        is: containerRenderer.value ?? "div",
        props: {
          photos: photos.value,
          layoutOptions: layoutOptions.value
        }
      };
    });
    const layoutComponent = computed(() => {
      if (isUnknownLayout.value) {
        return void 0;
      }
      switch (layout.value) {
        case Rows:
          return {
            is: _sfc_main$5,
            props: {
              photos: photos.value,
              layoutOptions: layoutOptions.value,
              rowRenderer: rowRenderer.value
            }
          };
        case Columns:
          return {
            is: _sfc_main$3,
            props: {
              photos: photos.value,
              layoutOptions: layoutOptions.value,
              columnRenderer: columnRenderer.value
            }
          };
        case Masonry:
          return {
            is: _sfc_main$2,
            props: {
              photos: photos.value,
              layoutOptions: layoutOptions.value,
              columnRenderer: columnRenderer.value
            }
          };
        default:
          return void 0;
      }
    });
    const handlePhotoClick = (event, ctx) => {
      const photo = ctx.photo;
      const index2 = ctx.layout.index;
      emit("click", { event, photo, index: index2 });
    };
    watch(
      [
        containerWidth,
        layout,
        targetRowHeight,
        rowConstraints,
        columns,
        spacing,
        padding,
        sizes,
        containerRenderer,
        rowRenderer,
        columnRenderer,
        photoRenderer
      ],
      ([
        containerWidthValue,
        layoutValue,
        targetRowHeightValue,
        rowConstraintsValue,
        columnsValue,
        spacingValue,
        paddingValue,
        sizesValue
      ]) => {
        layoutOptions.value = resolveLayoutOptions({
          containerWidth: containerWidthValue ?? 0,
          layout: layoutValue,
          targetRowHeight: targetRowHeightValue,
          rowConstraints: rowConstraintsValue,
          columns: columnsValue,
          spacing: spacingValue,
          padding: paddingValue,
          sizes: sizesValue
        });
      },
      {
        immediate: true
      }
    );
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(containerComponent.value.is), mergeProps({
        class: className2.value,
        style: style.value
      }, containerComponent.value.props, {
        ref_key: "containerRef",
        ref: containerRef
      }), {
        default: withCtx(() => [
          isUnknownLayout.value ? (openBlock(), createElementBlock("div", _hoisted_1, "Unknown Layout")) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [
            layoutComponent.value ? (openBlock(), createBlock(resolveDynamicComponent(layoutComponent.value.is), normalizeProps(mergeProps({ key: 0 }, layoutComponent.value.props)), {
              default: withCtx((slotContext) => [
                createVNode(_sfc_main$1, mergeProps(slotContext, {
                  renderer: unref(photoRenderer),
                  clickable: _ctx.$props.onClick !== void 0,
                  onClick: ($event) => handlePhotoClick($event, slotContext)
                }), null, 16, ["renderer", "clickable", "onClick"])
              ]),
              _: 1
            }, 16)) : createCommentVNode("", true)
          ], 64))
        ]),
        _: 1
      }, 16, ["class", "style"]);
    };
  }
});
const index = {
  install(app) {
    app.component("PhotoAlbum", _sfc_main);
  }
};
export {
  LayoutTypes,
  _sfc_main as PhotoAlbum,
  index as default
};
